import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Search, FileText, Check } from "lucide-react";
import { clsx } from "clsx";
import { api } from "@/api/client";
import type { DocumentListResponse } from "@/types/document";

export interface AttachedDoc {
  id: string;
  judul: string;
}

interface Props {
  selected: AttachedDoc[];
  onClose: () => void;
  onSave: (docs: AttachedDoc[]) => void;
}

export default function AttachDocumentsModal({ selected, onClose, onSave }: Props) {
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<Map<string, AttachedDoc>>(
    () => new Map(selected.map((d) => [d.id, d])),
  );

  const { data, isLoading } = useQuery<DocumentListResponse>({
    queryKey: ["documents-attach", search],
    queryFn: () => api.get("/documents/", { page: 1, page_size: 50 }),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data?.items ?? [];
    return (data?.items ?? []).filter(
      (d) =>
        d.judul.toLowerCase().includes(q) ||
        (d.nomor_dokumen ?? "").toLowerCase().includes(q),
    );
  }, [data, search]);

  const toggle = (doc: { id: string; judul: string }) => {
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(doc.id)) next.delete(doc.id);
      else next.set(doc.id, { id: doc.id, judul: doc.judul });
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[80vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Lampirkan Dokumen</h2>
            <p className="text-xs text-gray-500">
              Pilih dokumen untuk dijadikan konteks chat
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="border-b border-gray-100 px-5 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari dokumen…"
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {isLoading ? (
            <p className="text-sm text-gray-500">Memuat…</p>
          ) : filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-400">
              Tidak ada dokumen.
            </p>
          ) : (
            <ul className="space-y-1">
              {filtered.map((doc) => {
                const isOn = picked.has(doc.id);
                return (
                  <li key={doc.id}>
                    <button
                      type="button"
                      onClick={() => toggle(doc)}
                      className={clsx(
                        "flex w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors",
                        isOn
                          ? "border-blue-300 bg-blue-50"
                          : "border-gray-200 bg-white hover:bg-gray-50",
                      )}
                    >
                      <FileText
                        className={clsx(
                          "mt-0.5 h-4 w-4 flex-shrink-0",
                          isOn ? "text-blue-600" : "text-gray-400",
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-900">
                          {doc.judul}
                        </p>
                        {doc.nomor_dokumen && (
                          <p className="truncate text-xs text-gray-500">
                            {doc.nomor_dokumen}
                          </p>
                        )}
                        <div className="mt-1 flex flex-wrap gap-1.5 text-xs">
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">
                            {doc.jenis_pnbp_kode}
                          </span>
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">
                            {doc.status_nama}
                          </span>
                        </div>
                      </div>
                      {isOn && <Check className="h-4 w-4 text-blue-600" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 px-5 py-4">
          <p className="text-xs text-gray-500">{picked.size} dokumen dipilih</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Batal
            </button>
            <button
              type="button"
              onClick={() => onSave(Array.from(picked.values()))}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Lampirkan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
